import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from "chart.js";
import API from "../api";
import BackButton from "./common/BackButton";
import { SEMANTIC, UI } from "../styles/theme";

ChartJS.register(
  LineElement, BarElement, PointElement,
  LinearScale, CategoryScale, Tooltip, Legend
);

const fmt = (n) =>
  n != null
    ? Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" })
    : "N/A";

export default function TickerDetail() {
  const { ticker } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    API.get(`/ticker/${ticker}/chart`)
      .then((res) => setData(res.data))
      .catch(() => setError("Impossibile caricare i dati per " + ticker));
  }, [ticker]);

  if (error) {
    return (
      <div style={styles.page}>
        <BackButton />
        <p style={{ color: SEMANTIC.actionSell, textAlign: "center" }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.page}>
        <BackButton />
        <p style={{ textAlign: "center", color: SEMANTIC.textSecondary }}>Caricamento...</p>
      </div>
    );
  }

  const labels = data.history.map((h) => h.date);
  const prices = data.history.map((h) => h.price);
  const volumes = data.history.map((h) => h.volume);

  const changePositive = data.change_pct != null && data.change_pct >= 0;
  const changeColor = changePositive ? SEMANTIC.actionBuy : SEMANTIC.actionSell;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, datalabels: { display: false } },
    scales: {
      x: { ticks: { color: "#aaa", maxTicksLimit: 8 }, grid: { color: "rgba(255,255,255,0.05)" } },
      y: { ticks: { color: "#aaa" }, grid: { color: "rgba(255,255,255,0.1)" } },
    },
  };

  return (
    <div style={styles.page}>
      <BackButton />

      {/* Header */}
      <div style={styles.header}>
        <img
          src={`${process.env.PUBLIC_URL}/logos/${ticker}.png`}
          alt={ticker}
          style={styles.logo}
          onError={(e) => { e.target.onerror = null; e.target.src = `${process.env.PUBLIC_URL}/logos/default-logo.png`; }}
        />
        <div>
          <h2 style={{ color: SEMANTIC.actionBuy, margin: 0 }}>
            {ticker}
            {data.long_name && (
              <span style={{ color: SEMANTIC.textSecondary, fontSize: "16px", fontWeight: "normal", marginLeft: "10px" }}>
                — {data.long_name}
              </span>
            )}
          </h2>
          <div style={{ display: "flex", alignItems: "baseline", gap: "12px", marginTop: "4px" }}>
            <span style={{ fontSize: "28px", fontWeight: "bold", color: SEMANTIC.textPrimary }}>
              {fmt(data.current_price)}
            </span>
            {data.change_pct != null && (
              <span style={{ color: changeColor, fontWeight: "bold", fontSize: "16px" }}>
                {changePositive ? "+" : ""}{data.change_pct.toFixed(2)}%
                {"  "}({changePositive ? "+" : ""}{fmt(data.change)})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Grafico prezzi */}
      <div style={styles.card}>
        <h5 style={styles.cardTitle}>Andamento prezzi (90 giorni)</h5>
        <div style={{ height: 260 }}>
          <Line
            data={{
              labels,
              datasets: [{
                data: prices,
                borderColor: "#00FF88",
                borderWidth: 2,
                tension: 0.25,
                pointRadius: 0,
                fill: false,
              }],
            }}
            options={chartOptions}
          />
        </div>
      </div>

      {/* Grafico volumi */}
      <div style={styles.card}>
        <h5 style={styles.cardTitle}>Volumi (90 giorni)</h5>
        <div style={{ height: 180 }}>
          <Bar
            data={{
              labels,
              datasets: [{
                data: volumes,
                backgroundColor: "rgba(0, 170, 255, 0.5)",
                borderColor: "#00aaff",
                borderWidth: 1,
              }],
            }}
            options={{
              ...chartOptions,
              scales: {
                ...chartOptions.scales,
                y: {
                  ...chartOptions.scales.y,
                  ticks: {
                    color: "#aaa",
                    callback: (v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : v,
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    backgroundColor: SEMANTIC.backgroundApp,
    color: SEMANTIC.textPrimary,
    minHeight: "100vh",
    padding: UI.spacing.xl,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
  },
  logo: {
    width: "56px",
    height: "56px",
    borderRadius: "10%",
    border: "1px solid #ccc",
    objectFit: "contain",
    backgroundColor: "#f9f9f9",
  },
  card: {
    backgroundColor: SEMANTIC.backgroundCard,
    borderRadius: UI.borderRadius.lg,
    padding: UI.spacing.lg,
    marginBottom: "20px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.5)",
  },
  cardTitle: {
    color: SEMANTIC.textSecondary,
    fontSize: "13px",
    marginBottom: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
};
